/**
 * @fileoverview Zod schemas and types for Ad Spends domain.
 * @module features/ad-spends/schemas/adSpendsSchema
 */

import { z } from 'zod'

/**
 * Platform enum aligned with API contract.
 */
export const platformSchema = z.enum(['META', 'GOOGLE', 'TIKTOK', 'OTHER'])
export type Platform = z.infer<typeof platformSchema>

/**
 * Country enum aligned with API contract.
 */
export const countrySchema = z.enum([
  'PL',
  'CZ',
  'SK',
  'HU',
  'RO',
  'BG',
  'HR',
  'SI',
  'LT',
  'LV',
  'EE',
  'IE',
  'SE',
  'DE',
  'AT',
  'NL',
  'FR',
  'IT',
  'PT',
  'UNKNOWN',
])
export type Country = z.infer<typeof countrySchema>

/**
 * SKU DTO schema (subset used in AdSpend).
 */
export const skuSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  description: z.string().optional().nullable().catch(undefined),
  category: z.string().optional().nullable().catch(undefined),
  brand: z.string().optional().nullable().catch(undefined),
  model: z.string().optional().nullable().catch(undefined),
  cost: z.number().optional().nullable().catch(undefined),
  // costCurrency in OpenAPI is an object, but not required for table display
  // Keep flexible to avoid validation brittleness across backends
  costCurrency: z.unknown().optional().nullable().catch(undefined),
})
export type SKU = z.infer<typeof skuSchema>

/**
 * Currency object is an object in API; we only display code.
 * Keep it permissive to accept both string code or object with currencyCode.
 */
export const currencySchema = z.union([
  z.string(),
  z.object({
    currencyCode: z.string(),
  }),
])

/**
 * AdSpend DTO schema aligned with OpenAPI.
 */
export const adSpendSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  platform: platformSchema,
  country: countrySchema,
  date: z.string(), // "YYYY-MM-DD"
  amount: z.number(),
  currency: currencySchema,
  sku: skuSchema,
  url: z.string().optional().nullable().catch(undefined),
})
export type AdSpendDTO = z.infer<typeof adSpendSchema>

/**
 * Paginated response wrapper.
 */
export const paginatedAdSpendsSchema = z.object({
  content: z.array(adSpendSchema),
  totalElements: z.number(),
  totalPages: z.number(),
  size: z.number(),
  number: z.number(),
  first: z.boolean().optional().catch(undefined),
  last: z.boolean().optional().catch(undefined),
})
export type AdSpendsResponse = z.infer<typeof paginatedAdSpendsSchema>

/**
 * Query params schema (for internal type derivation).
 */
export const adSpendsParamsSchema = z.object({
  page: z.number().int().min(0).optional(),
  size: z.number().int().min(1).optional(),
  sort: z.string().optional(),
  direction: z.enum(['asc', 'desc']).optional(),
  startDate: z.union([z.string(), z.date()]).optional(),
  endDate: z.union([z.string(), z.date()]).optional(),
  skus: z.array(z.string()).optional(),
  platforms: z.array(platformSchema).optional(),
  countries: z.array(countrySchema).optional(),
})
export type AdSpendsParams = z.infer<typeof adSpendsParamsSchema>
